import { forwardRef, useId } from 'react';
import { strings } from '../lib/strings';
import { ClearIcon, SearchIcon } from './Icons';

interface Props {
  value: string;
  onChange: (value: string) => void;
  onSubmit: (value: string) => void;
  onClear: () => void;
  size: 'hero' | 'compact';
}

export const UniverseSearch = forwardRef<HTMLInputElement, Props>(function UniverseSearch(
  { value, onChange, onSubmit, onClear, size },
  ref
) {
  const id = useId();
  const hero = size === 'hero';
  const hasValue = value.length > 0;

  return (
    <form
      role="search"
      className="relative w-full"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(value);
      }}
    >
      <label htmlFor={id} className="sr-only">
        {strings.searchLabel}
      </label>
      <SearchIcon
        className={`pointer-events-none absolute top-1/2 -translate-y-1/2 text-subtle ${
          hero ? 'left-6 h-[20px] w-[20px]' : 'left-3 h-4 w-4'
        }`}
      />
      <input
        ref={ref}
        id={id}
        type="search"
        enterKeyHint="search"
        value={value}
        data-size={size}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            if (value) {
              e.preventDefault();
              onClear();
            } else {
              e.currentTarget.blur();
            }
          }
        }}
        placeholder={hero ? strings.searchPlaceholder : strings.searchShort}
        autoComplete="off"
        spellCheck={false}
        className={`search-field w-full appearance-none text-ink placeholder:text-subtle transition-[box-shadow,background-color] duration-base ease-standard ${
          hero
            ? 'h-[60px] rounded-full bg-white pl-16 pr-16 text-lg shadow-raised'
            : 'h-[38px] rounded-sm bg-fill pl-8 pr-8 text-base'
        }`}
      />
      <button
        type="button"
        aria-label={strings.clearSearch}
        tabIndex={hasValue ? 0 : -1}
        onClick={onClear}
        className={`absolute top-1/2 flex -translate-y-1/2 items-center justify-center rounded-full text-subtle transition-[opacity,transform] duration-base ease-spring hover:text-muted ${
          hero ? 'right-4 h-8 w-8' : 'right-1 h-8 w-8'
        } ${hasValue ? 'scale-100 opacity-100' : 'pointer-events-none scale-50 opacity-0'}`}
      >
        <ClearIcon className={hero ? 'h-[20px] w-[20px]' : 'h-4 w-4'} />
      </button>
    </form>
  );
});
